import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { CONTACT } from "@/lib/veyro";

export const VeyroHeader = () => {
  const { openCart, pricing } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const goTo = (hash) => {
    if (location.pathname !== "/") navigate("/" + hash);
    else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-[#090A0E]/80 border-b border-slate-800/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="text-xl font-black tracking-tighter text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          VEYRO<span className="text-[#00E5FF]">™</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
          <Link data-testid="nav-product" to="/product" className="hover:text-white transition-colors">Product</Link>
          <button data-testid="nav-features" onClick={() => goTo("#features")} className="hover:text-white transition-colors">Features</button>
          <button data-testid="nav-specs" onClick={() => goTo("#specs")} className="hover:text-white transition-colors">Specs</button>
          <button data-testid="nav-faq" onClick={() => goTo("#faq")} className="hover:text-white transition-colors">FAQ</button>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/product" data-testid="header-shop-btn" className="hidden sm:inline-flex items-center justify-center bg-white text-[#090A0E] hover:bg-[#00E5FF] rounded-full px-5 py-2 text-sm font-bold transition-colors duration-200">
            Shop Now
          </Link>
          <button data-testid="header-cart-btn" onClick={openCart} className="relative p-2 text-slate-300 hover:text-[#00E5FF] transition-colors" aria-label="Open cart">
            <ShoppingBag size={21} strokeWidth={1.8} />
            {pricing.count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-1 -right-1 bg-[#00E5FF] text-[#090A0E] text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {pricing.count}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export const VeyroFooter = () => (
  <footer data-testid="site-footer" className="bg-[#05060A] border-t border-slate-800/60">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-12">
      <div>
        <div className="text-2xl font-black tracking-tighter text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          VEYRO<span className="text-[#00E5FF]">™</span>
        </div>
        <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xs">
          Luxury performance, engineered to move. The vacuum compression travel backpack for people in motion.
        </p>
      </div>
      <div>
        <div className="mono-label text-xs text-slate-500 font-bold">Policies</div>
        <ul className="mt-4 space-y-3 text-sm">
          {[
            ["/policies/shipping-policy", "Shipping Policy", "footer-link-shipping"],
            ["/policies/refund-policy", "Returns & Refunds", "footer-link-returns"],
            ["/policies/privacy-policy", "Privacy Policy", "footer-link-privacy"],
            ["/policies/terms-of-service", "Terms of Service", "footer-link-terms"],
          ].map(([to, label, tid]) => (
            <li key={to}>
              <Link data-testid={tid} to={to} className="text-slate-400 hover:text-[#00E5FF] transition-colors">{label}</Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="mono-label text-xs text-slate-500 font-bold">Contact</div>
        <a data-testid="footer-contact-email" href={`mailto:${CONTACT.email}`} className="mt-4 block text-sm text-slate-400 hover:text-[#00E5FF] transition-colors">
          {CONTACT.email}
        </a>
      </div>
    </div>
    <div className="border-t border-slate-800/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 text-xs text-slate-600 flex flex-col sm:flex-row justify-between gap-2">
        <span>© {new Date().getFullYear()} VEYRO™. All rights reserved.</span>
        <span>Secure checkout powered by Shopify</span>
      </div>
    </div>
  </footer>
);
